import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { Toast } from "primereact/toast";
import React, { useState } from "react";
import styled from "styled-components";

import { useBulkChangeClaimPayerMutation } from "../../store/queries/claims";
import { useGetAllPayersQuery } from "../../store/queries/payers";
import { ClaimBulkTarget } from "../../types/claim";
import {
  bulkErrorDetail,
  claimCount,
  useBulkActionToast,
} from "./bulkActionShared";

interface IBulkEditPayerModalProps {
  visible: boolean;
  onHide: () => void;
  /** The selected rows, or every claim matching the list's filters (CLE-3035). */
  target: ClaimBulkTarget;
  /** How many claims `target` covers — for the copy only. */
  count: number;
  accountId: string;
  /** Called after a successful bulk update so the parent can clear selection / refresh state. */
  onSuccess: () => void;
}

interface IPayerOption {
  name: string;
  id: number | null;
}

// Match the single-claim PayerSelector's "No Match" sentinel.
const NO_MATCH_OPTION: IPayerOption = { name: "No Payer Match", id: null };

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 28rem;
`;

const Hint = styled.div`
  color: #6c757d;
  font-size: 0.875rem;
`;

/**
 * CLE-2172: confirm-and-apply modal for bulk matched-payer change.
 *
 * Reuses the same payer autocomplete pattern as the single-claim PayerSelector,
 * including the "No Payer Match" sentinel for un-matching claims. CLE-3035: on
 * any number of claims; the endpoint answers 202 and the change runs in the
 * background on the EDI worker (each claim re-prices its child events), so the
 * modal closes at once and the banner above the claims list shows progress.
 */
export function BulkEditPayerModal({
  visible,
  onHide,
  target,
  count,
  accountId,
  onSuccess,
}: IBulkEditPayerModalProps) {
  const { localToast, show } = useBulkActionToast();
  const [searchValue, setSearchValue] = useState<string | IPayerOption>("");
  const [chosenPayer, setChosenPayer] = useState<IPayerOption | null>(null);
  const [filteredPayers, setFilteredPayers] = useState<IPayerOption[]>([]);

  const { data: payersData } = useGetAllPayersQuery();
  const [bulkChangePayer, { isLoading: isStarting }] =
    useBulkChangeClaimPayerMutation();

  const payers: IPayerOption[] = (payersData?.data || payersData || []).map(
    (p: any) => ({ name: p.name, id: p.id })
  );

  const onPayerSearch = (e: any) => {
    const query = e.query.toLowerCase();
    const matchingPayers = payers.filter((payer) =>
      payer.name.toLowerCase().includes(query)
    );
    setFilteredPayers([NO_MATCH_OPTION, ...matchingPayers]);
  };

  const handleApply = async () => {
    if (!chosenPayer) return;

    try {
      await bulkChangePayer({ ...target, payerId: chosenPayer.id }).unwrap();
      show({
        severity: "success",
        summary: "Payer update started",
        detail: `Payer update started for ${claimCount(
          count
        )}. It runs in the background — progress shows above the list and we'll let you know here when it finishes.`,
        life: 8000,
      });

      // Reset local form state but let the parent close the modal +
      // clear selection through onSuccess.
      setSearchValue("");
      setChosenPayer(null);
      onSuccess();
    } catch (err: any) {
      show({
        severity: "error",
        summary: "Could not start the payer update",
        detail: bulkErrorDetail(err),
        life: 8000,
      });
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        disabled={isStarting}
      />
      <Button
        label={`Apply to ${count.toLocaleString("en-US")}`}
        icon="pi pi-check"
        onClick={handleApply}
        disabled={!chosenPayer || isStarting}
        loading={isStarting}
      />
    </div>
  );

  return (
    <>
      <Toast ref={localToast} />
      <Dialog
        header="Update matched payer"
        visible={visible}
        onHide={isStarting ? () => {} : onHide}
        closable={!isStarting}
        footer={footer}
        // Account context is only used to scope the payer list in the future;
        // for now `useGetAllPayersQuery` returns the full set.
        data-account-id={accountId}
      >
        <Body>
          <div>
            Pick a payer to apply to <strong>{claimCount(count)}</strong>. Each
            claim will be reprocessed with the new payer.
          </div>

          <AutoComplete
            value={searchValue}
            suggestions={filteredPayers}
            field="name"
            completeMethod={onPayerSearch}
            onChange={(e) => setSearchValue(e.value)}
            onSelect={(e) => {
              const next = e.value as IPayerOption;
              setSearchValue(next);
              setChosenPayer(next);
            }}
            placeholder="Search payers..."
            dropdown
            disabled={isStarting}
            inputStyle={{ width: "100%" }}
            style={{ width: "100%" }}
          />

          {chosenPayer && chosenPayer.id === null && (
            <Message
              severity="info"
              text="Selected claims will be marked unmatched and excluded from payer analysis."
            />
          )}

          <Hint>
            Each claim re-runs its full pricing pipeline. This runs in the
            background — you can keep working.
          </Hint>
        </Body>
      </Dialog>
    </>
  );
}
